/**
 * emulator-ports.mjs — the ONE place the Firebase emulator port block is defined.
 *
 * Why a fixed, non-default block (2026-09-05): the platform repo's Caddy ingress owns
 * 127.0.0.1:8080 on the i9, which is also the Firebase emulator's Firestore default.
 * The nightly web-e2e stage silently self-skipped because a stranger held the port.
 * So every machine now uses the same shifted block, and aswb-backend/firebase.json
 * declares the identical numbers. Change them HERE and in that file, nowhere else.
 *
 * This module is imported by BOTH node scripts and the browser bundle, so it must stay
 * pure: no `process`, no node builtins. Callers pass their own env bag in.
 */

/** The fixed block. Must match aswb-backend/firebase.json `emulators`. */
export const DEFAULT_EMULATOR_PORTS = Object.freeze({
  firestore: 8380,
  functions: 5301,
  auth: 9399,
  hub: 4700,
  ui: 4000,
});

export const DEFAULT_EMULATOR_HOST = '127.0.0.1';

/**
 * Env var names per emulator, in precedence order. The `VITE_`-prefixed names exist
 * because only those reach the browser bundle through Vite.
 */
const PORT_ENV_KEYS = Object.freeze({
  firestore: ['FIRESTORE_EMULATOR_PORT', 'VITE_FIRESTORE_EMULATOR_PORT'],
  functions: ['FUNCTIONS_EMULATOR_PORT', 'VITE_FUNCTIONS_EMULATOR_PORT'],
  auth: ['AUTH_EMULATOR_PORT', 'VITE_AUTH_EMULATOR_PORT'],
  hub: ['EMULATOR_HUB_PORT', 'VITE_EMULATOR_HUB_PORT'],
  ui: ['EMULATOR_UI_PORT', 'VITE_EMULATOR_UI_PORT'],
});

/** The three emulators the web app actually talks to (the UI/hub are infrastructure). */
export const CONNECTED_EMULATORS = Object.freeze(['firestore', 'functions', 'auth']);

const toPort = (value, fallback) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 && parsed < 65536 ? parsed : fallback;
};

/**
 * Resolve the port block from an env bag, falling back to DEFAULT_EMULATOR_PORTS.
 * @param {Record<string, string | undefined>} [env]
 */
export function resolveEmulatorPorts(env = {}) {
  const ports = {};
  for (const [name, keys] of Object.entries(PORT_ENV_KEYS)) {
    const found = keys.map((key) => env[key]).find((value) => value !== undefined && value !== '');
    ports[name] = toPort(found, DEFAULT_EMULATOR_PORTS[name]);
  }
  return ports;
}

/** @param {Record<string, string | undefined>} [env] */
export function resolveEmulatorHost(env = {}) {
  const host = env.EMULATOR_HOST || env.VITE_FIREBASE_EMULATOR_HOST;
  return (host && host.trim()) || DEFAULT_EMULATOR_HOST;
}

/**
 * The env block the Firebase Admin SDK + firebase-tools read. Seeding scripts, the
 * emulator itself and the Vite dev server all need the SAME numbers, so build it once.
 * @param {Record<string, string | undefined>} [env]
 */
export function emulatorEnv(env = {}) {
  const ports = resolveEmulatorPorts(env);
  const host = resolveEmulatorHost(env);
  return {
    FIRESTORE_EMULATOR_HOST: `${host}:${ports.firestore}`,
    FIREBASE_AUTH_EMULATOR_HOST: `${host}:${ports.auth}`,
    FUNCTIONS_EMULATOR_HOST: `${host}:${ports.functions}`,
    FIREBASE_EMULATOR_HUB: `${host}:${ports.hub}`,
    // Passed through to firebase-tools so the backend repo boots on the same block
    // even if its firebase.json is momentarily out of step with this file.
    FIRESTORE_EMULATOR_PORT: String(ports.firestore),
    FUNCTIONS_EMULATOR_PORT: String(ports.functions),
    AUTH_EMULATOR_PORT: String(ports.auth),
    EMULATOR_HUB_PORT: String(ports.hub),
    EMULATOR_UI_PORT: String(ports.ui),
    // The browser bundle only sees VITE_-prefixed vars.
    VITE_FIRESTORE_EMULATOR_PORT: String(ports.firestore),
    VITE_FUNCTIONS_EMULATOR_PORT: String(ports.functions),
    VITE_AUTH_EMULATOR_PORT: String(ports.auth),
    VITE_FIREBASE_EMULATOR_HOST: host,
  };
}
